import { Box, Typography, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { AnalyticsColors, thSx, tdSx, trHoverSx, stateMessageSx } from './analyticsUi';

export interface DrillDownColumn<T> {
    label: string;
    render: (item: T) => React.ReactNode;
    width?: string;
    align?: 'left' | 'right' | 'center';
}

interface DrillDownPanelProps<T> {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    items: T[];
    columns: DrillDownColumn<T>[];
    renderActions?: (item: T) => React.ReactNode;
}

function DrillDownPanel<T>({
    open,
    onClose,
    title,
    subtitle,
    items,
    columns,
    renderActions,
}: DrillDownPanelProps<T>) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ pb: 0.5 }}>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                    <div>
                        <span style={{ fontWeight: 700, fontSize: '16px' }}>{title}</span>
                        {subtitle && (
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: AnalyticsColors.textSecondary,
                                    fontWeight: 400,
                                    mt: '2px',
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </div>
                    <IconButton onClick={onClose} size="small">
                        <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ px: 2, pb: 2 }}>
                {items.length === 0 ? (
                    <Box sx={stateMessageSx}>No items to show</Box>
                ) : (
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                {columns.map((col, i) => (
                                    <Box
                                        component="th"
                                        key={i}
                                        sx={thSx({ align: col.align })}
                                        style={col.width ? { width: col.width } : undefined}
                                    >
                                        {col.label}
                                    </Box>
                                ))}
                                {renderActions && (
                                    <Box component="th" sx={thSx({ align: 'right' })}>
                                        Actions
                                    </Box>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, rowIdx) => (
                                <Box component="tr" key={rowIdx} sx={trHoverSx}>
                                    {columns.map((col, colIdx) => (
                                        <Box
                                            component="td"
                                            key={colIdx}
                                            sx={tdSx({ align: col.align })}
                                        >
                                            {col.render(item)}
                                        </Box>
                                    ))}
                                    {renderActions && (
                                        <Box component="td" sx={tdSx({ align: 'right' })}>
                                            {renderActions(item)}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </tbody>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default DrillDownPanel;
